import { useState, useEffect } from 'react'
import { persistor } from 'app/store'
import CustomButton from 'components/Buttons/CustomButton'
import { useFetchReadInboxQuery } from 'features/private-views/dashboard/dashboardApiSlice'
import {
  setIsCustomDate,
  setPaginationTag,
  toggleModalInbox,
} from 'features/private-views/dashboard/dashboardSlice'
import { logout } from 'features/public-views/login/loginSlice'
import { useGrantCompliancePermissionMutation } from 'features/private-views/findora-dashboard/findoraDashboardListApiSlice'
import {
  LogOut,
  X,
  Bell,
  Info,
  ChevronRight,
  Shield,
  Check,
  CheckCircle,
  AlertCircle,
} from 'react-feather'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Modal } from 'reactstrap'
import { formatDatePMBV2 } from 'utils/formatDate'
import {
  URL_TNC_PRIVATE,
} from 'utils/pathUrl'
import { handleNotificationClick } from 'utils/notificationHelper'

const ModalInbox = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { isOpenModalInbox, dataInbox, profile, ownerData } = useSelector(
    (s) => s.dashboard,
  )
  const ownerUserId = ownerData?.userId
  const toggle = () => dispatch(toggleModalInbox())
  let extraData = dataInbox?.extraData

  const [grantPermission, { isLoading: isGranting }] =
    useGrantCompliancePermissionMutation()
  const [grantStatus, setGrantStatus] = useState('idle') // 'idle' | 'success' | 'error'
  const [grantErrorMessage, setGrantErrorMessage] = useState('')

  useEffect(() => {
    setGrantStatus('idle')
    setGrantErrorMessage('')
  }, [dataInbox?.id])

  useFetchReadInboxQuery(dataInbox?.id, {
    skip: !dataInbox?.id,
  })

  if (typeof extraData === 'string') {
    try {
      extraData = JSON.parse(extraData)
    } catch {
      // Failed to parse extraData
    }
  }

  const isCompliancePermissionRequest = Boolean(
    extraData?.action === 'COMPLIANCE_PERMISSION_ASK' ||
      extraData?.action === 'COMPLIANCE_PERMISSION' ||
      extraData?.schemeName === 'ESG_REPORT_DETAIL' ||
      extraData?.allowPayload?.schemeName === 'ESG_REPORT_DETAIL' ||
      extraData?.type === 'ESG_PERMISSION' ||
      (dataInbox?.title && /laporan\s*esg/i.test(dataInbox.title)) ||
      (dataInbox?.message && /akses.*laporan\s*esg/i.test(dataInbox.message))
  )

  const targetUserId = (() => {
    const rawId =
      extraData?.allowPayload?.userId ??
      extraData?.payload?.userId ??
      extraData?.userId ??
      extraData?.callerUserId ??
      extraData?.requesterUserId ??
      extraData?.senderUserId ??
      extraData?.body?.userId ??
      extraData?.data?.userId ??
      dataInbox?.senderUserId ??
      dataInbox?.userId ??
      null

    if (rawId !== null && rawId !== undefined && !isNaN(Number(rawId))) {
      return Number(rawId)
    }

    if (typeof extraData?.allowUrl === 'string') {
      const match = extraData.allowUrl.match(/[?&]userId=(\d+)/i)
      if (match) return Number(match[1])
    }

    return null
  })()

  const requesterName = (() => {
    if (extraData?.requesterName) return extraData.requesterName
    if (extraData?.userName) return extraData.userName
    if (extraData?.companyName) return extraData.companyName
    const match = dataInbox?.message?.match(/^(.*?)\s+meminta\s+akses/i)
    if (match && match[1]) return match[1].trim()
    return 'Pihak Pemohon'
  })()

  const handleGrantPermission = async () => {
    try {
      setGrantErrorMessage('')
      if (!targetUserId && !extraData?.allowPayload?.userId) {
        setGrantErrorMessage('ID pemohon tidak ditemukan dalam notifikasi.')
        setGrantStatus('error')
        return
      }

      const payload = {
        ...(extraData?.allowPayload || {}),
        userId: targetUserId,
        allowed: true,
      }

      await grantPermission(payload).unwrap()
      setGrantStatus('success')
    } catch (err) {
      setGrantStatus('error')
      setGrantErrorMessage(
        err?.data?.message ||
          err?.message ||
          'Gagal memberikan izin akses laporan ESG. Silakan coba lagi.'
      )
    }
  }

  const onClickLogout = async () => {
    await persistor.purge()
    localStorage.removeItem('transferInventoryFilters')
    localStorage.removeItem('defaultWarehouseInventory')
    localStorage.removeItem('lastFetchTime')
    dispatch(setIsCustomDate(false))
    dispatch(setPaginationTag({ selectedContact: undefined }))
    dispatch(logout())
    window.location.replace('/')
  }

  const handleClickItemNotif = () => {
    if (extraData) {
      handleNotificationClick(dataInbox, navigate, ownerUserId, dispatch, undefined)
    }
  }

  const hasValidAction = Boolean(
    extraData && (
      extraData.page === 'tnc' ||
      (extraData.action === 'AGENT_CONFIRMATION_PAGE' && extraData.id) ||
      extraData.escrowpaymentId ||
      extraData.escrowPaymentId ||
      extraData.payableId ||
      extraData.userPinjamanId ||
      extraData.tradeShortCutId ||
      extraData.orderId
    )
  );

  const handleToggle = () => {
    if (extraData?.block && !dataInbox?.userdoconsent) {
      // do nothing if blocked
    } else {
      toggle()
    }
  }

  return (
    <>
      <Modal
        centered
        isOpen={isOpenModalInbox}
        toggle={handleToggle}
        contentClassName="border-0 shadow-lg"
        style={{ borderRadius: '24px', overflow: 'hidden' }}
        data-testid="navbar-inbox-modal"
      >
        <div className="position-relative p-4 p-sm-5">
          {(!extraData?.block || dataInbox?.userdoconsent) && (
            <div 
              className="position-absolute d-flex align-items-center justify-content-center" 
              style={{ top: '20px', right: '20px', width: '32px', height: '32px', cursor: 'pointer', borderRadius: '50%', backgroundColor: '#f1f5f9', transition: 'background 0.2s', zIndex: 10 }}
              data-testid="navbar-inbox-modal-close"
              onClick={handleToggle}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e2e8f0'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f1f5f9'}
            >
              <X size={18} className="text-secondary" />
            </div>
          )}

          {isCompliancePermissionRequest ? (
            grantStatus === 'success' ? (
              <div className="d-flex flex-column align-items-center text-center py-2">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center mb-3"
                  style={{
                    width: '64px',
                    height: '64px',
                    backgroundColor: '#edf8f1',
                    color: '#25734f',
                    border: '1px solid #d2eadd',
                  }}
                >
                  <CheckCircle size={34} />
                </div>
                <h4 className="fw-bold text-dark mb-2" style={{ fontSize: '18px' }}>
                  Permintaan Berhasil Diterima
                </h4>
                <p className="text-secondary mb-4" style={{ fontSize: '0.925rem', lineHeight: '1.6', maxWidth: '360px' }}>
                  Izin akses detail laporan ESG telah berhasil diberikan kepada{' '}
                  <strong className="text-dark">{requesterName}</strong>. Sekarang pemohon dapat melihat rincian data metrik Anda.
                </p>
                <CustomButton
                  color="primary"
                  size="md"
                  className="w-100 fw-semibold rounded-pill py-2.5 shadow-sm"
                  style={{
                    backgroundColor: '#006386',
                    borderColor: '#006386',
                    fontSize: '14px',
                  }}
                  onClick={handleToggle}
                >
                  Selesai & Tutup
                </CustomButton>
              </div>
            ) : (
              <>
                {/* 1. HEADER & ICON */}
                <div className="d-flex flex-column align-items-center text-center mb-4 pe-0 mt-1">
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 mb-3"
                    style={{
                      width: '68px',
                      height: '68px',
                      background: 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)',
                      color: '#006386',
                      boxShadow: '0 8px 20px -4px rgba(0, 99, 134, 0.25)',
                    }}
                  >
                    <Shield size={32} strokeWidth={2.2} />
                  </div>
                  <div
                    className="text-uppercase fw-bold mb-1"
                    style={{
                      fontSize: '11px',
                      letterSpacing: '0.8px',
                      color: '#006386',
                    }}
                  >
                    Permintaan Izin Akses
                  </div>
                  <h4 className="fw-bold text-dark mb-1" style={{ letterSpacing: '-0.3px', lineHeight: '1.3', fontSize: '19px' }}>
                    {dataInbox?.title || 'Permintaan Akses Laporan ESG'}
                  </h4>
                  <small className="text-muted fw-medium" style={{ fontSize: '0.85rem' }}>
                    {formatDatePMBV2(dataInbox?.createdAt ?? dataInbox?.scheduledTime)}
                  </small>
                </div>

                {/* 2. REQUESTER IDENTITY CARD */}
                <div
                  className="rounded-3 mb-4 text-start"
                  style={{
                    backgroundColor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    padding: '16px',
                  }}
                >
                  <div
                    className="d-flex align-items-center gap-2 pb-3 mb-3 border-bottom"
                    style={{ borderColor: '#edf2f7' }}
                  >
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center fw-bold flex-shrink-0"
                      style={{
                        width: '38px',
                        height: '38px',
                        backgroundColor: '#e0f2fe',
                        color: '#0284c7',
                        fontSize: '14px',
                      }}
                    >
                      {requesterName.slice(0, 2).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-grow-1">
                      <div className="fw-bold text-dark text-truncate" style={{ fontSize: '14.5px' }}>
                        {requesterName}
                      </div>
                      <div className="text-muted" style={{ fontSize: '11.5px' }}>
                        Pihak Pemohon Akses
                      </div>
                    </div>
                    <span
                      className="badge rounded-pill fw-medium"
                      style={{
                        backgroundColor: '#edf4f8',
                        color: '#006386',
                        fontSize: '11px',
                        padding: '4px 10px',
                      }}
                    >
                      Laporan ESG
                    </span>
                  </div>

                  <p className="text-secondary mb-3" style={{ fontSize: '13.5px', lineHeight: '1.6' }}>
                    {dataInbox?.message || `${requesterName} meminta akses ke detail laporan ESG Anda.`}
                  </p>

                  <div
                    className="rounded-2 d-flex align-items-start gap-2"
                    style={{
                      backgroundColor: '#ffffff',
                      border: '1px solid #e2e8f0',
                      padding: '12px 14px',
                      fontSize: '12px',
                      color: '#64748b',
                      lineHeight: '1.5',
                    }}
                  >
                    <Info size={15} className="text-primary flex-shrink-0 mt-0.5" />
                    <span>
                      Memberikan izin akan memungkinkan pemohon melihat penjelasan kuantitatif dan rincian parameter kepatuhan ESG Anda.
                    </span>
                  </div>
                </div>

                {/* ERROR ALERT */}
                {grantStatus === 'error' && (
                  <div
                    className="alert alert-danger py-2 px-3 mb-3 d-flex align-items-center gap-2 text-start"
                    style={{ fontSize: '12.5px' }}
                  >
                    <AlertCircle size={16} className="flex-shrink-0" />
                    <span>{grantErrorMessage}</span>
                  </div>
                )}

                {/* 3. ACTION BUTTONS */}
                <div className="d-flex flex-column gap-2">
                  <CustomButton
                    color="primary"
                    size="md"
                    className="w-100 fw-semibold rounded-pill py-2.5 shadow-sm d-flex align-items-center justify-content-center gap-2"
                    style={{
                      backgroundColor: '#006386',
                      borderColor: '#006386',
                      fontSize: '14px',
                      cursor: isGranting ? 'not-allowed' : 'pointer',
                    }}
                    disabled={isGranting}
                    onClick={handleGrantPermission}
                  >
                    {isGranting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden="true"
                          style={{ width: '15px', height: '15px' }}
                        />
                        <span>Memproses Persetujuan...</span>
                      </>
                    ) : (
                      <>
                        <Check size={17} strokeWidth={2.5} />
                        <span>Terima Permintaan Akses</span>
                      </>
                    )}
                  </CustomButton>

                  <button
                    type="button"
                    className="btn btn-sm btn-link text-muted text-decoration-none py-1 fw-medium"
                    style={{ fontSize: '13px' }}
                    disabled={isGranting}
                    onClick={handleToggle}
                  >
                    Nanti Saja
                  </button>
                </div>
              </>
            )
          ) : (
            // DEFAULT NOTIFICATION VIEW
            <>
              <div className="d-flex flex-column align-items-center text-center mb-4 pe-0 mt-2">
                <div 
                  className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 mb-3" 
                  style={{ width: '64px', height: '64px', backgroundColor: '#e0f2fe', color: '#0284c7' }}
                >
                  <Bell size={28} />
                </div>
                <div>
                  <h4 className="fw-bold text-dark mb-2" style={{ letterSpacing: '-0.3px', lineHeight: '1.3' }}>{dataInbox?.title}</h4>
                  <small className="text-muted fw-medium" style={{ fontSize: '0.85rem' }}>{formatDatePMBV2(dataInbox?.createdAt ?? dataInbox?.scheduledTime)}</small>
                </div>
              </div>
              
              {dataInbox?.image && (
                <div className="mb-4 rounded-4 overflow-hidden border mx-auto" style={{ borderColor: '#f1f5f9', maxWidth: '100%' }}>
                  <img
                    src={dataInbox?.image}
                    className="img-fluid w-100"
                    style={{ maxHeight: '200px', objectFit: 'cover' }}
                    alt="Notification"
                  />
                </div>
              )}

              <div className="p-3 p-sm-4 rounded-4 mb-4" style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <p className="text-secondary mb-0 text-center" style={{ fontSize: '1rem', lineHeight: '1.6' }}>
                  {dataInbox?.message}
                </p>
              </div>

              <div className="mb-0">
                {Number(ownerData?.userId) !== Number(profile?.userId) &&
                  dataInbox?.userConsent &&
                  !dataInbox?.userdoconsent && (
                    <div 
                      className="p-3 rounded-4 mb-4 d-flex gap-2 align-items-start"
                      style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b' }}
                    >
                      <Info size={18} className="flex-shrink-0 mt-1" />
                      <small className="fw-medium" style={{ lineHeight: '1.5' }}>
                        Hanya PIC yang memiliki wewenang untuk memberikan persetujuan. Silakan menghubungi PIC terkait untuk proses persetujuan.
                      </small>
                    </div>
                  )}
                {Number(ownerData?.userId) !== Number(profile?.userId) &&
                  dataInbox?.userConsent &&
                  !dataInbox?.userdoconsent &&
                  extraData?.page === 'tnc' &&
                  extraData?.block && (
                    <CustomButton
                      className="mb-4 fw-semibold rounded-pill py-2 w-100 d-flex align-items-center justify-content-center gap-2"
                      size="md"
                      color="danger"
                      outline
                      testId="navbar-inbox-modal-logout"
                      onClick={() => {
                        toggle()
                        onClickLogout()
                      }}
                      block
                    >
                      <LogOut size={16} />
                      <span>Keluar</span>
                    </CustomButton>
                  )}
              </div>
              
              <div>
                {hasValidAction && 
                ((dataInbox?.extraData && Number(ownerData?.userId) === Number(profile?.userId)) ||
                  (dataInbox?.extraData &&
                    !Object.prototype.hasOwnProperty.call(
                      dataInbox,
                      'userConsent',
                    ))) && (
                  <CustomButton
                    color="primary"
                    size="md"
                    className="w-100 fw-semibold rounded-pill py-2 shadow-sm d-flex align-items-center justify-content-center gap-2"
                    testId="navbar-inbox-modal-detail"
                    onClick={() => {
                      if (extraData.page === 'tnc') {
                        navigate(URL_TNC_PRIVATE, {
                          state: { ...dataInbox, extraData: extraData },
                        })
                        toggle()
                      } else {
                        handleClickItemNotif()
                        toggle()
                      }
                    }}
                    block
                  >
                    <span style={{ letterSpacing: '0.3px' }}>Lihat Detail</span>
                    <ChevronRight size={16} />
                  </CustomButton>
                )}
              </div>
            </>
          )}
        </div>
      </Modal>
    </>
  )
}

export default ModalInbox
